import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import * as api from "../api/monitors.js";

export function useMonitors(params, options = {}) {
  return useQuery({
    queryKey: keys.monitors.list(params),
    queryFn: () => api.fetchMonitors(params),
    ...options,
  });
}

/**
 * Cursor-paginated monitors list with server-side filtering. `params` carries
 * the filters (type, active, q, limit) — never a cursor; pagination is driven
 * by the page param so changing a filter resets to the first page.
 */
export function useMonitorsInfinite(params, options = {}) {
  return useInfiniteQuery({
    queryKey: keys.monitors.list(params),
    queryFn: ({ pageParam }) =>
      api.fetchMonitors({ ...params, cursor: pageParam ?? undefined }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.meta?.next_cursor ?? undefined,
    // Keep the current results visible while a filter change refetches, so the
    // list doesn't blank out on every keystroke / filter toggle.
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useMonitor(id, options = {}) {
  return useQuery({
    queryKey: keys.monitors.detail(id),
    queryFn: () => api.fetchMonitor(id),
    enabled: !!id,
    ...options,
  });
}

export function useMonitorTemplates(options = {}) {
  return useQuery({
    queryKey: ["monitor-templates"],
    queryFn: api.fetchMonitorTemplates,
    staleTime: 10 * 60_000,
    ...options,
  });
}

export function useLocations(options = {}) {
  return useQuery({
    queryKey: keys.locations.list,
    queryFn: api.fetchLocations,
    staleTime: 30 * 60_000,
    ...options,
  });
}

export function useMonitorChecks(id, params, options = {}) {
  return useQuery({
    queryKey: keys.monitors.checks(id, params),
    queryFn: () => api.fetchMonitorChecks(id, params),
    enabled: !!id,
    ...options,
  });
}

export function useUptimeBuckets(id, params, options = {}) {
  return useQuery({
    queryKey: keys.monitors.uptime(id, params),
    queryFn: () => api.fetchUptimeBuckets(id, params),
    enabled: !!id,
    ...options,
  });
}

export function useCreateMonitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createMonitor,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.monitors.all }),
  });
}

export function useUpdateMonitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateMonitor(id, data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: keys.monitors.all });
      qc.invalidateQueries({ queryKey: keys.monitors.detail(id) });
    },
  });
}

export function useDeleteMonitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteMonitor,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.monitors.all }),
  });
}

export function useTriggerMonitorCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.triggerMonitorCheck,
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.monitors.detail(id) });
      qc.invalidateQueries({ queryKey: ["monitors", id] });
    },
  });
}
